import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Toast from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { empresaService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';

const EMPTY = { ruc: '', razon_social: '', nombre_comercial: '' };

export default function Empresas() {
  const { user } = useAuth();
  const esAdmin = user?.id_perfil === 1;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [toast, setToast] = useState(null);

  // Copia de configuración
  const [copiaOrigen, setCopiaOrigen] = useState(null);        // empresa origen seleccionada
  const [copiaDestino, setCopiaDestino] = useState('');        // id_empresa destino
  const [copiaGrupos,  setCopiaGrupos]  = useState(null);      // catálogo del backend
  const [copiaSeleccion, setCopiaSeleccion] = useState(new Set());
  const [copiando, setCopiando] = useState(false);
  const [copiaResultado, setCopiaResultado] = useState(null);

  // Aplicar plantilla
  const [plantillaInfo, setPlantillaInfo] = useState(null);
  const [plantillaTarget, setPlantillaTarget] = useState(null);
  const [aplicandoPlantilla, setAplicandoPlantilla] = useState(false);
  const [plantillaResultado, setPlantillaResultado] = useState(null);

  // Gestión de sedes
  const [sedesTarget, setSedesTarget] = useState(null);        // empresa cuyas sedes se editan
  const [sedes, setSedes] = useState([]);
  const [sedeForm, setSedeForm] = useState({ codigo: '', nombre: '', direccion: '', ubigeo: '', _editId: null });
  const [sedeSaving, setSedeSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await empresaService.getAll();
      setRecords(r.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar empresas.') });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!esAdmin) return;
    empresaService.plantillaInfo()
      .then(r => setPlantillaInfo(r.data))
      .catch(() => setPlantillaInfo({ existe: false }));
  }, [esAdmin]);

  const abrirAplicarPlantilla = (empresa) => {
    setPlantillaTarget(empresa);
    setPlantillaResultado(null);
  };
  const cerrarPlantilla = () => { setPlantillaTarget(null); setPlantillaResultado(null); };

  const ejecutarAplicarPlantilla = async () => {
    if (!plantillaTarget) return;
    setAplicandoPlantilla(true);
    try {
      const r = await empresaService.aplicarPlantilla(plantillaTarget.id_empresa);
      setPlantillaResultado(r.data);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al aplicar plantilla.') });
    } finally { setAplicandoPlantilla(false); }
  };

  const openNew = () => { setForm(EMPTY); setShowModal(true); };
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  /* ── Copia de configuración ── */
  const abrirCopia = async (empresaOrigen) => {
    setCopiaOrigen(empresaOrigen);
    setCopiaDestino('');
    setCopiaResultado(null);
    try {
      const r = await empresaService.tablasCopiables();
      setCopiaGrupos(r.data.grupos);
      // Por defecto: todas marcadas
      const all = new Set();
      Object.values(r.data.grupos).forEach(arr => arr.forEach(t => all.add(t)));
      setCopiaSeleccion(all);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error cargando catálogo.') });
      setCopiaOrigen(null);
    }
  };
  const cerrarCopia = () => { setCopiaOrigen(null); setCopiaResultado(null); };

  const toggleTablaCopia = (t) => setCopiaSeleccion(prev => {
    const n = new Set(prev);
    if (n.has(t)) n.delete(t); else n.add(t);
    return n;
  });
  const toggleGrupoCopia = (grupo) => {
    const tablas = copiaGrupos[grupo] || [];
    setCopiaSeleccion(prev => {
      const n = new Set(prev);
      const todas = tablas.every(t => n.has(t));
      tablas.forEach(t => { if (todas) n.delete(t); else n.add(t); });
      return n;
    });
  };

  /* ── Sedes por empresa ── */
  const abrirSedes = async (empresa) => {
    setSedesTarget(empresa);
    setSedeForm({ codigo: '', nombre: '', direccion: '', ubigeo: '', _editId: null });
    try {
      const r = await empresaService.listarSedes(empresa.id_empresa);
      setSedes(r.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar sedes.') });
      setSedesTarget(null);
    }
  };
  const cerrarSedes = () => { setSedesTarget(null); setSedes([]); };

  const setSedeF = (k, v) => setSedeForm(prev => ({ ...prev, [k]: v }));

  const guardarSede = async (e) => {
    e.preventDefault();
    if (!sedeForm.codigo.trim() || !sedeForm.nombre.trim()) {
      setAlert({ type: 'warning', message: 'Código y nombre son obligatorios.' }); return;
    }
    setSedeSaving(true);
    try {
      const payload = {
        codigo: sedeForm.codigo.trim(),
        nombre: sedeForm.nombre.trim(),
        direccion: sedeForm.direccion.trim() || null,
        ubigeo: sedeForm.ubigeo.trim() || null,
      };
      if (sedeForm._editId) {
        await empresaService.editarSede(sedesTarget.id_empresa, sedeForm._editId, payload);
        setToast({ type: 'success', message: 'Sede actualizada.' });
      } else {
        await empresaService.crearSede(sedesTarget.id_empresa, payload);
        setToast({ type: 'success', message: 'Sede creada.' });
      }
      const r = await empresaService.listarSedes(sedesTarget.id_empresa);
      setSedes(r.data || []);
      setSedeForm({ codigo: '', nombre: '', direccion: '', ubigeo: '', _editId: null });
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar sede.') });
    } finally { setSedeSaving(false); }
  };

  const editarSede = (s) => {
    setSedeForm({
      codigo: s.codigo || '', nombre: s.nombre || '',
      direccion: s.direccion || '', ubigeo: s.ubigeo || '',
      _editId: s.id_sucursal,
    });
  };

  const eliminarSede = async (s) => {
    if (!window.confirm(`¿Eliminar la sede "${s.nombre}"?`)) return;
    try {
      await empresaService.eliminarSede(sedesTarget.id_empresa, s.id_sucursal);
      const r = await empresaService.listarSedes(sedesTarget.id_empresa);
      setSedes(r.data || []);
      setToast({ type: 'success', message: 'Sede eliminada.' });
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al eliminar.') });
    }
  };

  const ejecutarCopia = async () => {
    if (!copiaDestino) { setAlert({ type: 'warning', message: 'Seleccione la empresa destino.' }); return; }
    if (copiaSeleccion.size === 0) { setAlert({ type: 'warning', message: 'Seleccione al menos una tabla.' }); return; }
    setCopiando(true);
    setCopiaResultado(null);
    try {
      const r = await empresaService.copiar({
        id_empresa_origen:  copiaOrigen.id_empresa,
        id_empresa_destino: parseInt(copiaDestino, 10),
        tablas: [...copiaSeleccion],
      });
      setCopiaResultado(r.data);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al copiar.') });
    } finally { setCopiando(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!/^\d{11}$/.test(form.ruc)) { setAlert({ type: 'warning', message: 'RUC debe tener 11 dígitos.' }); return; }
    if (!form.razon_social.trim()) { setAlert({ type: 'warning', message: 'La razón social es obligatoria.' }); return; }
    setSaving(true);
    try {
      await empresaService.create({
        ruc: form.ruc.trim(),
        razon_social: form.razon_social.trim(),
        nombre_comercial: (form.nombre_comercial || form.razon_social).trim(),
      });
      setToast({ type: 'success', message: 'Empresa creada correctamente. Puede reiniciar sesión para acceder a ella.' });
      setShowModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al crear empresa.') });
    } finally { setSaving(false); }
  };

  return (
    <Layout title="Empresas" subtitle="Administración">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0 }}>Empresas registradas</h2>
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>
            Cada empresa se crea con su propia base de datos aislada. Al crear una empresa,
            obtienes acceso automático como administrador.
          </div>
        </div>
        {esAdmin && (
          <button className="btn btn-primary btn-sm" onClick={openNew}>+ Nueva Empresa</button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
      ) : records.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Sin empresas.</div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>ID</th>
                <th style={{ width: 140 }}>RUC</th>
                <th>Razón social</th>
                <th>Nombre comercial</th>
                {esAdmin && <th>Base de datos</th>}
                {esAdmin && <th style={{ width: 180 }}>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {records.map(e => (
                <tr key={e.id_empresa}>
                  <td style={{ fontFamily: 'monospace', color: '#64748b' }}>#{e.id_empresa}</td>
                  <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#93c5fd' }}>{e.ruc}</td>
                  <td>{e.razon_social}</td>
                  <td style={{ color: '#94a3b8' }}>{e.nombre_comercial || '—'}</td>
                  {esAdmin && (
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#a78bfa' }}>
                      {e.database_name || `SISCORP_${e.ruc}`}
                    </td>
                  )}
                  {esAdmin && (
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => abrirSedes(e)}
                          title="Gestionar sedes de esta empresa"
                          style={{ background: 'rgba(96,165,250,0.12)', color: '#93c5fd', borderColor: 'rgba(96,165,250,0.3)' }}>
                          ◉ Sedes
                        </button>
                        {plantillaInfo?.existe && (
                          <button className="btn btn-secondary btn-sm"
                            onClick={() => abrirAplicarPlantilla(e)}
                            title={`Aplicar plantilla (${plantillaInfo.total} registros)`}
                            style={{ background: 'rgba(34,197,94,0.1)', color: '#86efac', borderColor: 'rgba(34,197,94,0.3)' }}>
                            ⇣ Plantilla
                          </button>
                        )}
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => abrirCopia(e)}
                          disabled={records.length < 2}
                          title={records.length < 2 ? 'Se necesita al menos otra empresa' : 'Copiar configuración a otra empresa'}
                          style={{ background: 'rgba(167,139,250,0.1)', color: '#c4b5fd', borderColor: 'rgba(167,139,250,0.3)' }}>
                          ⇢ Copiar config.
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!esAdmin && (
        <div style={{
          marginTop: 14, padding: 14, borderRadius: 8,
          background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.2)',
          fontSize: 12, color: '#fdba74',
        }}>
          Solo los administradores pueden registrar nuevas empresas.
        </div>
      )}

      {showModal && esAdmin && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Nueva Empresa</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  Se creará con su propia base de datos aislada
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body" style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* ── Sección: Identificación tributaria ── */}
                <Section title="Identificación tributaria" color="#60a5fa" icon={<IconDoc/>}>
                  <Field
                    label="RUC" required
                    hint="11 dígitos — se usa como identificador único de la empresa"
                    rightSlot={<Counter current={form.ruc.length} total={11} valid={form.ruc.length === 11} />}
                  >
                    <InputField
                      value={form.ruc}
                      onChange={v => setF('ruc', v.replace(/\D/g, '').slice(0, 11))}
                      placeholder="20123456789" required autoFocus
                      leading={<IconId/>}
                      mono
                      maxLength={11}
                    />
                  </Field>
                </Section>

                {/* ── Sección: Datos comerciales ── */}
                <Section title="Datos comerciales" color="#a78bfa" icon={<IconBuilding/>}>
                  <Field label="Razón social" required hint="Nombre legal registrado en SUNAT">
                    <InputField
                      value={form.razon_social}
                      onChange={v => setF('razon_social', v)}
                      placeholder="Ej: NUEVA EMPRESA S.A.C." required
                      leading={<IconBuildingSm/>}
                    />
                  </Field>
                  <Field label="Nombre comercial" hint="Opcional — se usa la razón social si se omite">
                    <InputField
                      value={form.nombre_comercial}
                      onChange={v => setF('nombre_comercial', v)}
                      placeholder="Ej: Nueva Empresa"
                      leading={<IconTag/>}
                    />
                  </Field>
                </Section>

                {/* Panel informativo del resultado */}
                <div style={{
                  padding: '12px 14px', borderRadius: 10,
                  background: 'linear-gradient(180deg, rgba(96,165,250,0.1), rgba(96,165,250,0.04))',
                  border: '1px solid rgba(96,165,250,0.25)',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(96,165,250,0.18)', color: '#93c5fd',
                  }}>
                    <IconDb/>
                  </div>
                  <div style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                    <div style={{ color: '#93c5fd', fontWeight: 700, marginBottom: 3 }}>
                      Al crear esta empresa:
                    </div>
                    <div style={{ color: '#cbd5e1' }}>
                      • Se generará la base de datos
                      <code style={{
                        margin: '0 4px', padding: '1px 7px', borderRadius: 4,
                        fontFamily: 'Consolas, monospace', fontSize: 10.5,
                        background: 'rgba(167,139,250,0.12)', color: '#c4b5fd',
                        border: '1px solid rgba(167,139,250,0.3)',
                      }}>SISCORP_{form.ruc || '<RUC>'}</code>
                      con todas sus tablas.<br/>
                      • Tu usuario obtendrá acceso automático de administrador.<br/>
                      • La operación puede tardar unos segundos.
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creando empresa...' : 'Crear Empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ Modal: Aplicar plantilla ═══ */}
      {plantillaTarget && esAdmin && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Aplicar plantilla</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  Destino: <strong style={{ color: '#93c5fd' }}>{plantillaTarget.razon_social}</strong>
                </div>
              </div>
              <button className="modal-close" onClick={cerrarPlantilla}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {plantillaInfo?.existe && (
                <div style={{
                  padding: 12, borderRadius: 10,
                  background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)',
                }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#86efac', marginBottom: 6 }}>
                    Plantilla disponible
                  </div>
                  <div style={{ fontSize: 11.5, color: '#cbd5e1', lineHeight: 1.6 }}>
                    Archivo: <code style={{ fontFamily: 'Consolas, monospace', fontSize: 10.5, color: '#c4b5fd' }}>{plantillaInfo.archivo}</code><br/>
                    Fuente: <strong style={{ color: '#e2e8f0' }}>{plantillaInfo.fuente?.razon_social}</strong> (RUC {plantillaInfo.fuente?.ruc})<br/>
                    Generada: <span style={{ color: '#94a3b8' }}>{plantillaInfo.generado?.replace('T', ' ').slice(0, 19)}</span><br/>
                    Total: <strong style={{ color: '#22c55e' }}>{plantillaInfo.total}</strong> registros en {Object.keys(plantillaInfo.por_tabla || {}).length} tablas
                  </div>
                </div>
              )}

              {!plantillaResultado ? (
                <div style={{
                  padding: 12, borderRadius: 10, fontSize: 11.5, color: '#cbd5e1', lineHeight: 1.6,
                  background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.2)',
                }}>
                  Se aplicarán los <strong>2083 registros</strong> de la plantilla a <strong>{plantillaTarget.razon_social}</strong>
                  en modo <strong>merge-por-código</strong>: los registros ya existentes se omiten,
                  nunca se sobrescriben.
                </div>
              ) : (
                <div style={{
                  padding: 14, borderRadius: 10,
                  background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)',
                }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#22c55e', marginBottom: 8 }}>
                    ✓ Plantilla aplicada
                  </div>
                  <div style={{ maxHeight: 240, overflowY: 'auto', fontSize: 11, fontFamily: 'Consolas, monospace' }}>
                    {Object.entries(plantillaResultado.reporte).map(([t, s]) => {
                      const hay = s.insertados > 0 || (s.errores || []).length > 0 || (s.fk_faltantes || []).length > 0;
                      const color = (s.errores || []).length > 0 || (s.fk_faltantes || []).length > 0
                        ? '#f87171' : (s.insertados > 0 ? '#93c5fd' : '#475569');
                      return (
                        <div key={t} style={{ display: 'flex', gap: 8, padding: '3px 0', color }}>
                          <span style={{ minWidth: 170 }}>{t}</span>
                          <span>+{s.insertados}</span>
                          <span>· omitidos {s.omitidos}</span>
                          {(s.errores || []).length > 0 && <span>· ERR {s.errores.length}</span>}
                          {(s.fk_faltantes || []).length > 0 && <span>· FK faltantes {s.fk_faltantes.length}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={cerrarPlantilla} disabled={aplicandoPlantilla}>
                {plantillaResultado ? 'Cerrar' : 'Cancelar'}
              </button>
              {!plantillaResultado && (
                <button type="button" className="btn btn-primary" onClick={ejecutarAplicarPlantilla}
                  disabled={aplicandoPlantilla}>
                  {aplicandoPlantilla ? 'Aplicando...' : 'Aplicar plantilla'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Modal: Sedes de la empresa ═══ */}
      {sedesTarget && esAdmin && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Sedes de la empresa</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  <strong style={{ color: '#93c5fd' }}>{sedesTarget.razon_social}</strong> · RUC {sedesTarget.ruc}
                </div>
              </div>
              <button className="modal-close" onClick={cerrarSedes}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Form de alta/edición */}
              <Section title={sedeForm._editId ? 'Editar sede' : 'Nueva sede'} color="#60a5fa" icon={<IconBuildingSm/>}>
                <form onSubmit={guardarSede} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10 }}>
                    <Field label="Código" required>
                      <InputField value={sedeForm.codigo} onChange={v => setSedeF('codigo', v)}
                        placeholder="0001" required mono />
                    </Field>
                    <Field label="Nombre" required>
                      <InputField value={sedeForm.nombre} onChange={v => setSedeF('nombre', v)}
                        placeholder="Ej: Lima" required leading={<IconBuildingSm/>} />
                    </Field>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 10 }}>
                    <Field label="Dirección" hint="Opcional">
                      <InputField value={sedeForm.direccion} onChange={v => setSedeF('direccion', v)}
                        placeholder="Av. Principal 123" />
                    </Field>
                    <Field label="Ubigeo" hint="Opcional">
                      <InputField value={sedeForm.ubigeo} onChange={v => setSedeF('ubigeo', v)}
                        placeholder="150101" mono />
                    </Field>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    {sedeForm._editId && (
                      <button type="button" className="btn btn-secondary btn-sm"
                        onClick={() => setSedeForm({ codigo: '', nombre: '', direccion: '', ubigeo: '', _editId: null })}
                        disabled={sedeSaving}>
                        Cancelar edición
                      </button>
                    )}
                    <button type="submit" className="btn btn-primary btn-sm" disabled={sedeSaving}
                      style={{ marginLeft: sedeForm._editId ? 0 : 'auto' }}>
                      {sedeSaving ? 'Guardando...' : (sedeForm._editId ? 'Guardar cambios' : '+ Agregar sede')}
                    </button>
                  </div>
                </form>
              </Section>

              {/* Listado actual */}
              <div>
                <div style={{
                  fontSize: 10.5, fontWeight: 700, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8,
                }}>
                  Sedes registradas ({sedes.length})
                </div>
                {sedes.length === 0 ? (
                  <div style={{
                    padding: 20, textAlign: 'center', color: '#fdba74',
                    background: 'rgba(251,146,60,0.08)', border: '1px dashed rgba(251,146,60,0.3)',
                    borderRadius: 8, fontSize: 12,
                  }}>
                    ⚠ Esta empresa aún no tiene sedes. Crea al menos una para que los usuarios puedan ingresar.
                  </div>
                ) : (
                  <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, overflow: 'hidden' }}>
                    <table className="data-table" style={{ width: '100%', margin: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 90 }}>Código</th>
                          <th>Nombre</th>
                          <th>Dirección</th>
                          <th style={{ width: 90 }}>Ubigeo</th>
                          <th style={{ width: 140 }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sedes.map(s => (
                          <tr key={s.id_sucursal}>
                            <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#93c5fd' }}>{s.codigo}</td>
                            <td>{s.nombre}</td>
                            <td style={{ color: '#94a3b8', fontSize: 11.5 }}>{s.direccion || '—'}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>{s.ubigeo || '—'}</td>
                            <td>
                              <div style={{ display: 'flex', gap: 5 }}>
                                <button className="btn btn-secondary btn-sm" onClick={() => editarSede(s)}>Editar</button>
                                <button className="btn btn-danger btn-sm" onClick={() => eliminarSede(s)}>Eliminar</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={cerrarSedes}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Modal: Copiar configuración ═══ */}
      {copiaOrigen && esAdmin && (
        <div className="modal-overlay">
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Copiar configuración</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  Desde: <strong style={{ color: '#93c5fd' }}>{copiaOrigen.razon_social}</strong>
                </div>
              </div>
              <button className="modal-close" onClick={cerrarCopia}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '16px 20px', maxHeight: '72vh', overflowY: 'auto' }}>

              {/* Empresa destino */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>
                  Empresa destino
                </div>
                <select value={copiaDestino} onChange={e => setCopiaDestino(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8,
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#e2e8f0', outline: 'none',
                  }}>
                  <option value="">— Seleccione la empresa destino —</option>
                  {records.filter(r => r.id_empresa !== copiaOrigen.id_empresa).map(r => (
                    <option key={r.id_empresa} value={r.id_empresa}>
                      {r.razon_social} — {r.ruc}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 4 }}>
                  Modo de copia: <strong>Solo agregar nuevos</strong> (no sobrescribe registros existentes).
                </div>
              </div>

              {/* Selección de tablas */}
              {copiaGrupos && Object.entries(copiaGrupos).map(([grupo, tablas]) => {
                const marcadas = tablas.filter(t => copiaSeleccion.has(t)).length;
                const todasMarc = marcadas === tablas.length && tablas.length > 0;
                const tituloGrupo = grupo === 'tablas_generales' ? 'Tablas generales'
                  : grupo === 'utilitarios' ? 'Utilitarios'
                  : grupo === 'contabilidad' ? 'Contabilidad' : grupo;
                const color = grupo === 'tablas_generales' ? '#60a5fa'
                  : grupo === 'utilitarios' ? '#fb923c'
                  : '#a78bfa';
                return (
                  <div key={grupo} style={{
                    marginBottom: 12, borderRadius: 10, overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.06)',
                    background: 'rgba(15,27,45,0.35)',
                  }}>
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', cursor: 'pointer',
                      background: `linear-gradient(90deg, ${color}22, transparent)`,
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      borderLeft: `3px solid ${color}`,
                    }}>
                      <input type="checkbox" checked={todasMarc}
                        onChange={() => toggleGrupoCopia(grupo)}
                        style={{ width: 16, height: 16, accentColor: color }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {tituloGrupo}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          {marcadas} de {tablas.length} seleccionadas
                        </div>
                      </div>
                    </label>
                    <div style={{ padding: '8px 14px 12px 14px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {tablas.map(t => {
                        const on = copiaSeleccion.has(t);
                        return (
                          <button key={t} type="button" onClick={() => toggleTablaCopia(t)}
                            style={{
                              padding: '4px 10px', fontSize: 11.5, fontWeight: 600,
                              borderRadius: 5, cursor: 'pointer',
                              background: on ? `${color}22` : 'rgba(255,255,255,0.03)',
                              color: on ? color : '#94a3b8',
                              border: `1px solid ${on ? color + '55' : 'rgba(255,255,255,0.08)'}`,
                            }}>
                            {on ? '✓ ' : ''}{t.replace(/_/g, ' ')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Resultado */}
              {copiaResultado && (
                <div style={{
                  marginTop: 14, padding: 14, borderRadius: 10,
                  background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)',
                }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#22c55e', marginBottom: 8 }}>
                    ✓ Copia completada — {copiaResultado.origen.razon_social} → {copiaResultado.destino.razon_social}
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: 11, fontFamily: 'monospace' }}>
                    {Object.entries(copiaResultado.reporte).map(([t, s]) => {
                      const hay = s.insertados > 0 || (s.arrastrados || 0) > 0 || (s.errores || []).length > 0;
                      return (
                        <div key={t} style={{
                          display: 'flex', gap: 8, padding: '3px 0',
                          color: (s.errores || []).length > 0 ? '#f87171' : (s.insertados > 0 ? '#93c5fd' : '#64748b'),
                        }}>
                          <span style={{ minWidth: 160 }}>{t}</span>
                          <span>+{s.insertados}</span>
                          {s.arrastrados > 0 && <span>· arr {s.arrastrados}</span>}
                          <span>· omitidos {s.omitidos}</span>
                          {(s.errores || []).length > 0 && <span>· ERR {s.errores.length}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={cerrarCopia} disabled={copiando}>
                {copiaResultado ? 'Cerrar' : 'Cancelar'}
              </button>
              {!copiaResultado && (
                <button type="button" className="btn btn-primary" onClick={ejecutarCopia}
                  disabled={copiando || !copiaDestino || copiaSeleccion.size === 0}>
                  {copiando ? 'Copiando...' : `Copiar ${copiaSeleccion.size} tabla${copiaSeleccion.size === 1 ? '' : 's'}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}


/* ── Subcomponentes de UI (consistentes con SiscontConexiones) ─── */

const Section = ({ title, color = '#60a5fa', icon, children }) => (
  <div style={{
    padding: '12px 14px 14px',
    background: 'rgba(15,27,45,0.45)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderLeft: `3px solid ${color}`,
    borderRadius: 10,
    display: 'flex', flexDirection: 'column', gap: 10,
  }}>
    <div style={{
      fontSize: 10.5, fontWeight: 800, color, textTransform: 'uppercase',
      letterSpacing: 0.8, display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ opacity: 0.9, display: 'inline-flex' }}>{icon}</span>
      {title}
    </div>
    {children}
  </div>
);

const Field = ({ label, required, hint, rightSlot, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <label style={{
        fontSize: 10.5, fontWeight: 700, color: '#94a3b8',
        textTransform: 'uppercase', letterSpacing: 0.6,
      }}>
        {label} {required && <span style={{ color: '#f87171' }}>*</span>}
      </label>
      {rightSlot}
    </div>
    {children}
    {hint && <div style={{ fontSize: 10.5, color: '#64748b' }}>{hint}</div>}
  </div>
);

const Counter = ({ current, total, valid }) => (
  <span style={{
    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
    fontFamily: 'Consolas, monospace',
    color: valid ? '#22c55e' : (current === 0 ? '#64748b' : '#fb923c'),
    background: valid ? 'rgba(34,197,94,0.1)' : (current === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(251,146,60,0.1)'),
    border: `1px solid ${valid ? 'rgba(34,197,94,0.3)' : (current === 0 ? 'rgba(255,255,255,0.08)' : 'rgba(251,146,60,0.3)')}`,
  }}>
    {current}/{total}
  </span>
);

const InputField = React.forwardRef(function InputField(
  { value, onChange, leading, mono, style = {}, ...rest }, ref
) {
  const [focus, setFocus] = React.useState(false);
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      height: 36, padding: 0, borderRadius: 8,
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${focus ? '#3b82f6' : 'rgba(255,255,255,0.09)'}`,
      boxShadow: focus ? '0 0 0 3px rgba(59,130,246,0.12)' : 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      overflow: 'hidden',
      ...style,
    }}>
      {leading && (
        <span style={{
          width: 34, height: '100%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: focus ? '#93c5fd' : '#64748b',
          background: 'rgba(15,27,45,0.4)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }}>
          {leading}
        </span>
      )}
      <input
        ref={ref}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          flex: 1, height: '100%', width: '100%',
          padding: '0 12px',
          background: 'transparent',
          border: 'none', outline: 'none',
          color: '#e2e8f0',
          fontSize: 13,
          fontFamily: mono ? 'Consolas, monospace' : 'Inter, sans-serif',
          letterSpacing: mono ? 1 : 0,
          boxSizing: 'border-box',
        }}
        {...rest}
      />
    </div>
  );
});

/* ── Íconos ─────────────────────────────────────────────── */
const IconBase = ({ children, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IconDoc      = () => <IconBase><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></IconBase>;
const IconId       = () => <IconBase><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="12" r="2.5"/><path d="M14 10h5M14 14h5"/></IconBase>;
const IconBuilding = () => <IconBase><path d="M3 21h18"/><path d="M6 21V7l6-4 6 4v14"/><path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01"/></IconBase>;
const IconBuildingSm = () => <IconBase><path d="M3 21h18M5 21V10.5M19 21V10.5"/><path d="M9 21V7l3-3 3 3v14"/></IconBase>;
const IconTag      = () => <IconBase><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><path d="M7 7h.01"/></IconBase>;
const IconDb       = () => <IconBase><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v6c0 1.66 4 3 9 3s9-1.34 9-3V5M3 11v6c0 1.66 4 3 9 3s9-1.34 9-3v-6"/></IconBase>;
